import { BayData, getFixtureCenter } from '@amzn/ihm-floorplan-core';
import { PickPathFeatureCollection, PickPathFeature } from './buildPickPathFeatureCollection';
import {
  PICK_PATH_OUTLINE_WIDTH,
  PICK_PATH_ARROW_SIZE,
  PICK_PATH_Z_INDEX,
  PICK_PATH_DEFAULT_COLOR,
} from './pickPathConstants';

/**
 * Builds a PickPathFeatureCollection from saved fixture ordering data and floorplan fixtures.
 * Sorts fixtures by sequenceOrder and draws line segments between consecutive fixtures.
 */
export function buildPickPathFromOrdering(
  fixtureOrderingMap: Map<string, number>,
  fixtureData: BayData[],
  zoneColor?: string,
): PickPathFeatureCollection {
  if (fixtureOrderingMap.size === 0) {
    return { type: 'FeatureCollection', features: [] };
  }

  // Build a lookup from fixtureId to BayData
  const fixtureById = new Map<string, BayData>();
  for (const f of fixtureData) {
    if (f.id) fixtureById.set(f.id, f);
  }

  // Sort fixtures by sequence order
  const sorted = Array.from(fixtureOrderingMap.entries())
    .sort((a, b) => a[1] - b[1])
    .map(([fixtureId]) => fixtureId)
    .filter((id) => fixtureById.has(id));

  if (sorted.length < 2) {
    return { type: 'FeatureCollection', features: [] };
  }

  const color = zoneColor ?? PICK_PATH_DEFAULT_COLOR;
  const features: PickPathFeature[] = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const fromFixture = fixtureById.get(sorted[i])!;
    const toFixture = fixtureById.get(sorted[i + 1])!;
    const from = getFixtureCenter(fromFixture);
    const to = getFixtureCenter(toFixture);

    features.push({
      id: `pick-path-view-seg-${i}`,
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [from, to],
      },
      properties: {
        category: 'pickPath',
        style: {
          outlineColor: color,
          outlineWidth: PICK_PATH_OUTLINE_WIDTH,
          outlineStyle: 'solid',
          showArrows: true,
          arrowPosition: 'middle',
          arrowSize: PICK_PATH_ARROW_SIZE,
          zIndex: PICK_PATH_Z_INDEX,
        },
      },
    });
  }

  return { type: 'FeatureCollection', features };
}
